import React, { createContext, useState, useContext, useEffect, useMemo, useRef } from 'react';
import { client } from '@/api/client';
import { supabase } from '@/api/supabaseClient';

const SUPER_ADMIN_EMAIL = 'marcusg.2387@gmail.com';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isLoadingPublicSettings, setIsLoadingPublicSettings] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [appPublicSettings, setAppPublicSettings] = useState(null);
  const [staffProfile, setStaffProfile] = useState(null);
  const [carrierId, setCarrierId] = useState(null);
  const [carrierPlan, setCarrierPlan] = useState(null);   // 'owner_op' | 'fleet'
  const [carrierName, setCarrierName] = useState(null);   // for sidebar branding
  const [carrierLogoUrl, setCarrierLogoUrl] = useState(null); // uploaded carrier logo, for sidebar branding
  // Whether the carrier has purchased the $15/mo AI Dispatch add-on
  // (migration 0018). Only meaningful for owner_op -- fleet's price
  // already assumes AI Dispatch is included, so it's never gated there.
  const [aiDispatchAddon, setAiDispatchAddon] = useState(false);
  // Supabase fires the PASSWORD_RECOVERY event the moment a reset-email
  // link establishes its temporary session -- authoritative regardless
  // of which path the browser actually landed on (see App.jsx's own
  // use of this: if Supabase's redirect_to falls back to the bare Site
  // URL instead of /reset-password, e.g. a Redirect URLs allowlist
  // mismatch, the recovery session used to silently drop the user into
  // the normal authenticated app -- Onboarding, whatever -- before
  // they ever set a new password).
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  // Mirror of the current authenticated user id in a ref so the
  // onAuthStateChange handler (registered ONCE in the mount effect)
  // can see the current value without closing over a stale snapshot.
  // Used to decide whether an auth event is the first sign-in
  // (spinner appropriate) vs a background re-auth (silent so modal
  // state survives tab focus changes).
  const currentUserIdRef = useRef(null);

  useEffect(() => {
    initSupabase();
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') setIsPasswordRecovery(true);
      if (!session) {
        currentUserIdRef.current = null;
        setUser(null); setIsAuthenticated(false);
        setCarrierId(null); setCarrierPlan(null); setCarrierName(null); setCarrierLogoUrl(null); setAiDispatchAddon(false);
        setIsLoadingAuth(false);
        return;
      }
      // Identity-based silent detection. Event-type branching was
      // brittle: supabase fires SIGNED_IN in additional cases beyond
      // first sign-in (tab focus token revalidation, bfcache restore,
      // session refresh), and any non-silent path flips
      // isLoadingAuth(true) which unmounts the WHOLE App tree --
      // including any open modal with unsaved data (Marcus's lost
      // Add-Driver form, canonical bug).
      //
      // Better rule: if we ALREADY have a user authenticated with
      // the same user id, this is a background event regardless of
      // its label. Treat it as silent. Only flip the loading flag
      // when the user id is new (or first arrival).
      const incomingUserId = session.user?.id || null;
      const silent =
        currentUserIdRef.current !== null &&
        currentUserIdRef.current === incomingUserId;
      loadSupabaseUser({ silent });
    });
    return () => sub?.subscription?.unsubscribe?.();
  }, []);

  const initSupabase = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) await loadSupabaseUser();
    else { setIsAuthenticated(false); setIsLoadingAuth(false); }
  };

  // silent=true: re-pull memberships + staff_profile WITHOUT toggling
  // isLoadingAuth, so the App tree stays mounted and downstream modals
  // (DriverFormModal etc.) keep their local state. Used for background
  // events: TOKEN_REFRESHED on tab focus, USER_UPDATED, refreshUser()
  // after CompanySettings save.
  // silent=false (default): full initial-load flow, toggles loading
  // spinner during the fetch -- right for actual sign-in events.
  const loadSupabaseUser = async ({ silent = false } = {}) => {
    if (!silent) setIsLoadingAuth(true);

    // STEP 1 — establish identity. ONLY this step can knock the user back
    // to unauthenticated. Everything below is supplemental enrichment and
    // must NEVER tank the session if it fails (network blip, RLS hiccup,
    // backfill race, etc). Lesson learned the hard way: a single try/catch
    // around all three queries means one bad query = forced logout.
    let currentUser;
    try {
      currentUser = await client.auth.me();
      setUser(currentUser);
      setIsAuthenticated(true);
      // Sync the ref BEFORE any further async work so the next
      // onAuthStateChange event (which could fire seconds later on
      // a tab focus) sees the current user id and goes silent.
      currentUserIdRef.current = currentUser?.id || null;
    } catch (e) {
      console.error('[AuthContext] client.auth.me() failed:', e);
      // A background re-check that fails (e.g. no network right as the app
      // resumes) must not sign out someone who is already signed in --
      // real sign-outs arrive via onAuthStateChange's no-session branch.
      if (silent && currentUserIdRef.current) return;
      currentUserIdRef.current = null;
      setUser(null); setIsAuthenticated(false); setCarrierId(null); setCarrierPlan(null); setCarrierName(null); setCarrierLogoUrl(null); setAiDispatchAddon(false); setStaffProfile(null);
      if (!silent) setIsLoadingAuth(false);
      return;
    }

    // STEP 2 — supplemental: carrier membership + plan. Isolated try/catch so
    // a query failure leaves the user signed in (just without a carrier).
    // PostgREST embedded `carriers(plan)` returns the joined plan field
    // (canonical feature-gating signal per migration 0015).
    try {
      const { data: m, error: mErr } = await supabase
        .from('memberships')
        .select('carrier_id, carriers(plan, name, logo_url, ai_dispatch_addon)')
        .eq('status', 'active').limit(1).maybeSingle();
      if (mErr) {
        console.error('[AuthContext] memberships query error:', mErr);
      }
      const nextCarrierId   = m?.carrier_id || null;
      const nextCarrierPlan = m?.carriers?.plan || null;
      const nextCarrierName = m?.carriers?.name || null;
      const nextCarrierLogoUrl = m?.carriers?.logo_url || null;
      const nextAiDispatchAddon = m?.carriers?.ai_dispatch_addon || false;
      console.log('[AuthContext] memberships ->', { nextCarrierId, nextCarrierPlan, nextCarrierName });
      setCarrierId(nextCarrierId);
      setCarrierPlan(nextCarrierPlan);
      setCarrierName(nextCarrierName);
      setCarrierLogoUrl(nextCarrierLogoUrl);
      setAiDispatchAddon(nextAiDispatchAddon);
    } catch (e) {
      console.error('[AuthContext] memberships fetch failed (non-fatal):', e);
      setCarrierId(null);
      setCarrierPlan(null);
      setCarrierName(null);
      setCarrierLogoUrl(null);
      setAiDispatchAddon(false);
    }

    // STEP 3 — supplemental: staff_profile. Drives legacy isStaff /
    // staffRole / ROLE_TAB_ACCESS code paths in Layout.jsx + AdminPanel.jsx
    // so accountants/dispatch/hr invitees don't get force-redirected to
    // /onboarding. Driver-role members deliberately have NO staff_profile
    // and DO flow through /onboarding to fill out their DriverProfile.
    // Wrapped separately so an RLS or network error can't undo step 1.
    try {
      const { data: sp } = await supabase
        .from('staff_profiles')
        .select('*')
        .eq('user_id', currentUser.id)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      setStaffProfile(sp || null);
    } catch (e) {
      console.error('[AuthContext] staff_profiles fetch failed (non-fatal):', e);
      setStaffProfile(null);
    }

    if (!silent) setIsLoadingAuth(false);
  };

  const refreshUser = async () => {
    // Silent refresh: callers (e.g. CompanySettings after saving the
    // carrier name) shouldn't trigger the app-level loading spinner
    // that unmounts the page they're on. Their own button state
    // already shows the busy indicator.
    await loadSupabaseUser({ silent: true });
  };

  const staffRole = useMemo(() => {
    if (!user) return null;
    if (user.email?.toLowerCase() === SUPER_ADMIN_EMAIL.toLowerCase()) return 'super_admin';
    if (staffProfile?.is_active && staffProfile?.staff_role) return staffProfile.staff_role;
    if (user.role === 'admin') return 'super_admin';
    return null;
  }, [user, staffProfile]);

  const isStaff = staffRole !== null;

  const logout = async () => {
    try { await client.auth.logout(); } catch { /* ignore */ }
    setUser(null); setIsAuthenticated(false); setCarrierId(null); setCarrierPlan(null); setCarrierName(null); setCarrierLogoUrl(null); setAiDispatchAddon(false);
    window.location.href = '/';
  };

  const navigateToLogin = () => {
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoadingAuth,
      isLoadingPublicSettings,
      isPasswordRecovery,
      authError,
      appPublicSettings,
      staffProfile,
      staffRole,
      isStaff,
      isSupabase: true,
      carrierId,
      hasCarrier: !!carrierId,
      carrierPlan,                                 // 'owner_op' | 'fleet' | null
      carrierName,                                 // for sidebar branding (live)
      carrierLogoUrl,                               // uploaded carrier logo; sidebar falls back to the default Pro Haul wheel when null
      aiDispatchAddon,                              // owner_op only: has the $15/mo AI Dispatch add-on been purchased
      isOwnerOp: carrierPlan === 'owner_op',
      isFleet:   carrierPlan === 'fleet' || carrierPlan === null,  // default to fleet pre-load
      logout,
      navigateToLogin,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
